/**
 * 校正座標の行・語（コードポイント）と UTF-16 オフセットの相互変換。
 * バックエンド `nola-backend/src/proofreading/rules/positions.ts` と同じ規則。
 */

export function normalizeProofreadingNewlines(text: string): string {
  return text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
}

/** 同一本文に対する改行正規化と行頭テーブルを呼び出し単位で再利用する */
export type ProofreadingTextCache = {
  normalized: string;
  lineStarts: readonly number[];
};

export function createProofreadingTextCache(text: string): ProofreadingTextCache {
  const normalized = normalizeProofreadingNewlines(text);
  return {
    normalized,
    lineStarts: buildLineUtf16Starts(normalized),
  };
}

export function buildLineUtf16Starts(normalized: string): number[] {
  const starts: number[] = [0];
  for (let i = 0; i < normalized.length; i += 1) {
    if (normalized.charCodeAt(i) === 10 /* \n */) {
      starts.push(i + 1);
    }
  }
  return starts;
}

function lineIndexForUtf16Abs(starts: readonly number[], utf16Abs: number): number {
  let lo = 0;
  let hi = starts.length - 1;
  while (lo <= hi) {
    const mid = Math.floor((lo + hi) / 2);
    if (starts[mid] <= utf16Abs) {
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return Math.max(0, hi);
}

function lineSliceOnNormalized(
  normalized: string,
  starts: readonly number[],
  lineIndex0: number,
): { lineStart: number; lineStr: string } | null {
  if (lineIndex0 < 0 || lineIndex0 >= starts.length) {
    return null;
  }
  const lineStart = starts[lineIndex0];
  const nextStart = lineIndex0 + 1 < starts.length ? starts[lineIndex0 + 1] : normalized.length;
  const lineEndExclusive =
    nextStart > 0 && normalized.charCodeAt(nextStart - 1) === 10
      ? nextStart - 1
      : nextStart;
  return {
    lineStart,
    lineStr: normalized.slice(lineStart, lineEndExclusive),
  };
}

function utf16ColToOneBasedCp(lineStr: string, colUtf16: number): number {
  let u = 0;
  let cp = 0;
  const chars = Array.from(lineStr);
  for (let i = 0; i < chars.length; i += 1) {
    const ch = chars[i];
    const cw = ch.length;
    if (u >= colUtf16) {
      return cp + 1;
    }
    if (u + cw > colUtf16) {
      return cp + 1;
    }
    u += cw;
    cp += 1;
  }
  return cp + 1;
}

/** UTF-16 絶対オフセット → 1 始まりの行・コードポイント語位置 */
export function lineAndOneBasedCpOnNormalized(
  normalized: string,
  utf16Abs: number,
  lineStarts?: readonly number[],
): { line: number; cp: number } {
  const safeAbs = Math.min(
    Math.max(0, utf16Abs),
    Math.max(0, normalized.length - 1),
  );
  const starts = lineStarts ?? buildLineUtf16Starts(normalized);
  const idx = lineIndexForUtf16Abs(starts, safeAbs);
  const sliced = lineSliceOnNormalized(normalized, starts, idx);
  if (!sliced) {
    return { line: 1, cp: 1 };
  }
  const col = Math.max(0, safeAbs - sliced.lineStart);
  return {
    line: idx + 1,
    cp: utf16ColToOneBasedCp(sliced.lineStr, col),
  };
}

/** 1 始まりの行・コードポイント語位置 → UTF-16 絶対オフセット。算出不能時は null */
export function utf16AbsAtLineAndOneBasedCp(
  normalized: string,
  line1: number,
  cp1: number,
  lineStarts?: readonly number[],
): number | null {
  const starts = lineStarts ?? buildLineUtf16Starts(normalized);
  const sliced = lineSliceOnNormalized(normalized, starts, line1 - 1);
  if (!sliced) {
    return null;
  }
  let cp = 0;
  let colUtf16 = 0;
  const chars = Array.from(sliced.lineStr);
  for (let i = 0; i < chars.length; i += 1) {
    const ch = chars[i];
    if (cp + 1 === cp1) {
      return sliced.lineStart + colUtf16;
    }
    cp += 1;
    colUtf16 += ch.length;
  }
  if (cp + 1 === cp1) {
    return sliced.lineStart + colUtf16;
  }
  return null;
}

export function offsetToLineWord(
  text: string,
  utf16Abs: number,
  cache?: ProofreadingTextCache,
): { line: number; word: number } {
  const normalized = cache?.normalized ?? normalizeProofreadingNewlines(text);
  const safeAbs = Math.max(0, Math.min(utf16Abs, normalized.length));
  if (normalized.length === 0) {
    return { line: 1, word: 1 };
  }
  const pos = lineAndOneBasedCpOnNormalized(
    normalized,
    Math.min(safeAbs, normalized.length - 1),
    cache?.lineStarts,
  );
  return { line: pos.line, word: pos.cp };
}

export function lineWordToOffset(
  text: string,
  line: number,
  word: number,
  cache?: ProofreadingTextCache,
): number | null {
  const normalized = cache?.normalized ?? normalizeProofreadingNewlines(text);
  return utf16AbsAtLineAndOneBasedCp(normalized, line, word, cache?.lineStarts);
}

/** 行・語から UTF-16 範囲 [start, end) を求める。targetText は検証にも使う */
export function utf16RangeForLineWord(
  text: string,
  line: number,
  word: number,
  targetText: string,
  cache?: ProofreadingTextCache,
): { start: number; end: number } | null {
  const start = lineWordToOffset(text, line, word, cache);
  if (start === null) {
    return null;
  }
  const end = start + targetText.length;
  return { start, end };
}

export function snippetMatchesAtLineWord(
  text: string,
  line: number,
  word: number,
  expectedText: string,
  cache?: ProofreadingTextCache,
): boolean {
  const normalized = cache?.normalized ?? normalizeProofreadingNewlines(text);
  const range = utf16RangeForLineWord(normalized, line, word, expectedText, cache);
  if (range === null) {
    return false;
  }
  return normalized.slice(range.start, range.end) === expectedText;
}

export function codePointLength(text: string): number {
  return [...text].length;
}
